import { films, type Film } from "@/mocks/films";

export type { Film };

export function getFilmById(id?: string): Film | undefined {
  return films.find((film) => film.id === id);
}

export function getFilmsByIds(ids: string[]): Film[] {
  return ids
    .map((id) => films.find((film) => film.id === id))
    .filter((film): film is Film => Boolean(film));
}

export function latestFilms(count?: number): Film[] {
  const sorted = [...films].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
  return typeof count === "number" ? sorted.slice(0, count) : sorted;
}

export function hotFilms(count?: number): Film[] {
  const sorted = [...films].sort((a, b) => b.views - a.views);
  return typeof count === "number" ? sorted.slice(0, count) : sorted;
}

export function topRatedFilms(count?: number): Film[] {
  const sorted = [...films].sort((a, b) => b.rating - a.rating);
  return typeof count === "number" ? sorted.slice(0, count) : sorted;
}

export function relateFilms(film: Film, count = 4): Film[] {
  return films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      const genreScore = item.genres.filter((genre) => film.genres.includes(genre)).length;
      const tagScore = item.tags.filter((tag) => film.tags.includes(tag)).length;
      return { item, score: genreScore * 2 + tagScore * 3 + item.rating / 10 };
    })
    .filter((entry) => entry.score > 1)
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map((entry) => entry.item);
}

export function searchFilms(keyword: string): Film[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return films.filter((film) => {
    const haystack = [
      film.title,
      film.originalTitle,
      film.director,
      film.year.toString(),
      film.quality,
      film.region,
      ...film.cast,
      ...film.genres,
      ...film.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export interface FilmFilter {
  genres: string[];
  year: string;
  region: string;
  sort: string;
}

export function filterFilms(list: Film[], filter: FilmFilter): Film[] {
  const result = list.filter((film) => {
    const genreMatch = filter.genres.length === 0 || film.genres.some((genre) => filter.genres.includes(genre));
    const yearMatch =
      filter.year === "全部" ||
      filter.year === "" ||
      (filter.year === "更早" ? film.year < 2021 : film.year.toString() === filter.year);
    const regionMatch = filter.region === "全部" || filter.region === "" || film.region === filter.region;
    return genreMatch && yearMatch && regionMatch;
  });

  return result.sort((a, b) => {
    if (filter.sort === "views") return b.views - a.views;
    if (filter.sort === "rating") return b.rating - a.rating;
    return b.releaseDate.localeCompare(a.releaseDate);
  });
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return views.toLocaleString("zh-CN");
}

export function formatDuration(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${hour} 小时 ${minute} 分`;
}